import {
  DIAGNOSTIC_AI_USES,
  DIAGNOSTIC_MOTIONS,
  DIAGNOSTIC_ROLES,
  DIAGNOSTIC_TEAM_SIZES,
  DIAGNOSTIC_TEXT_MAX,
  painsForRoles,
} from '@shared/prospect-diagnostic';
import { type DiagnosticDraft, type DiagnosticStepKey, type DraftPatch, toggle } from '@/lib/diagnostic-steps';
import { ChoiceGrid, DiagnosticGrowingText, DiagnosticInput, DiagnosticTextArea, QuestionTitle, SubQuestion } from '@/components/diagnostic/DiagnosticParts';

interface Props {
  step: DiagnosticStepKey;
  draft: DiagnosticDraft;
  onChange: (patch: DraftPatch) => void;
  /** Single-choice screens move on by themselves once answered. */
  onAnswered: () => void;
}

export const DiagnosticQuestion = ({ step, draft, onChange, onAnswered }: Props) => {
  switch (step) {
    case 'website':
      return (
        <>
          <QuestionTitle>Quel est le site de votre entreprise ?</QuestionTitle>
          {/* Asked by Benoit (03/11): say upfront what comes at the end of the questions. */}
          <p className="-mt-4 text-[17px] leading-relaxed text-domino-inkSoft">
            Domino vous propose quelques apps et façons de répondre à vos besoins.
          </p>
          <DiagnosticInput id="diag-website" label="Site web" value={draft.website} onChange={(website) => onChange({ website })} placeholder="acme.fr" inputMode="url" autoComplete="url" />
        </>
      );
    case 'role':
      return (
        <>
          <QuestionTitle>Quel est votre rôle ?</QuestionTitle>
          <ChoiceGrid
            options={DIAGNOSTIC_ROLES}
            isSelected={(key) => draft.role === key}
            // The team starts empty: the reader picks who sells, their own role included (Benoit, 03/11).
            onPick={(role) => { onChange({ role }); onAnswered(); }}
          />
        </>
      );
    case 'team':
      return (
        <>
          <QuestionTitle>Qui vend avec vous ?</QuestionTitle>
          <ChoiceGrid multiple options={DIAGNOSTIC_ROLES} isSelected={(key) => draft.teamRoles.includes(key)} onPick={(key) => onChange((current) => ({ teamRoles: toggle(current.teamRoles, key) }))} />
          <SubQuestion>Combien êtes-vous ?</SubQuestion>
          <ChoiceGrid options={DIAGNOSTIC_TEAM_SIZES} isSelected={(key) => draft.teamSize === key} onPick={(teamSize) => onChange({ teamSize })} />
        </>
      );
    case 'motions':
      return (
        <>
          <QuestionTitle>D’où viennent vos affaires ?</QuestionTitle>
          <ChoiceGrid multiple columns={1} options={DIAGNOSTIC_MOTIONS} isSelected={(key) => draft.motions.includes(key)} onPick={(key) => onChange((current) => ({ motions: toggle(current.motions, key) }))} />
        </>
      );
    case 'pains': {
      const pains = painsForRoles(draft.teamRoles);
      return (
        <>
          <QuestionTitle>Où part le temps ?</QuestionTitle>
          {DIAGNOSTIC_ROLES.filter((role) => pains.some((pain) => pain.role === role.key)).map((role) => (
            <div key={role.key} className="flex flex-col gap-3">
              <SubQuestion>{role.label}</SubQuestion>
              <ChoiceGrid
                multiple
                columns={1}
                options={pains.filter((pain) => pain.role === role.key)}
                isSelected={(key) => draft.pains.includes(key)}
                onPick={(key) => onChange((current) => ({ pains: toggle(current.pains, key) }))}
              />
              {/* A free text under each role (Benoit, 03/11): one line, no title, growing as needed. */}
              <DiagnosticGrowingText
                label={`Autre chose pour ${role.label}`}
                value={draft.painsOtherByRole?.[role.key] ?? ''}
                onChange={(text) => onChange((current) => ({ painsOtherByRole: { ...current.painsOtherByRole, [role.key]: text } }))}
                maxLength={DIAGNOSTIC_TEXT_MAX}
              />
            </div>
          ))}
        </>
      );
    }
    case 'ai':
      return (
        <>
          <QuestionTitle>Et l’IA, aujourd’hui ?</QuestionTitle>
          <ChoiceGrid columns={1} options={DIAGNOSTIC_AI_USES} isSelected={(key) => draft.aiUse === key} onPick={(aiUse) => onChange({ aiUse })} />
          <DiagnosticTextArea id="diag-ai-ideas" label="Vos idées" value={draft.aiIdeas} onChange={(aiIdeas) => onChange({ aiIdeas })} maxLength={DIAGNOSTIC_TEXT_MAX} />
        </>
      );
    case 'goal':
      return (
        <>
          <QuestionTitle>Vos objectifs pour ce trimestre ?</QuestionTitle>
          <DiagnosticTextArea id="diag-goal" label="Objectifs" value={draft.goal} onChange={(goal) => onChange({ goal })} maxLength={DIAGNOSTIC_TEXT_MAX} />
          <DiagnosticTextArea id="diag-problem" label="Le problème à régler en priorité ?" value={draft.problem ?? ''} onChange={(problem) => onChange({ problem })} maxLength={DIAGNOSTIC_TEXT_MAX} />
        </>
      );
  }
};
